import React, { useEffect, useState, useRef } from 'react';
import { WizardContext } from 'react-albus';
import { debounce } from 'lodash';
import { clearData, getExistingData, hasData } from '../helper';
import useApi from '../../_hooks/useApi';
import { FetchExpressWizard } from '../types';

import AssetAllocationChart from '../Components/AssetAllocationChart';
import RetirementChart from '../Components/RetirementChart';
import FormMenu from '../Components/FormMenu';
import ContactMenu from '../Components/ContactModal';
import { Disclosures } from '../Components/Disclosures';

const RETIREMENT_AGE = '65';
const initialState = {
  SubtitleText: '',
  AssetAllocationChart: {},
  RetirementChart: {
    Data: {
      Categories: [],
      Data: [],
    },
  },
  CurrentReturn: '',
  RecommendedReturn: '',
};

interface Props {
  wizard: WizardContext;
}

const Results: React.FC<Props> = ({ wizard: { replace } }) => {
  const [resultsData, setResultsData] = useState(initialState);
  const [retirementAge, setRetirementAge] = useState(RETIREMENT_AGE);
  const [isContactModalVisible, setIsContactModalVisible] = useState(false);
  const [isDisclosureModalVisibility, setDisclosureModalVisibility] = useState(false);

  const [callExpressWizardRetirement, { loading }] = useApi('/guest/ExpressWizardRetirement', {
    method: 'POST',
  });

  const fetchExpressWizard: FetchExpressWizard = (data, newRetirementAge = RETIREMENT_AGE) => {
    callExpressWizardRetirement({
      body: {
        ...data,
        RetirementAge: newRetirementAge,
        SpouseRetirementAge: newRetirementAge,
      },
      onSuccess: response => {
        setResultsData(response);
      },
    });
  };

  const debounceSetRetirementAge = useRef(debounce(fetchExpressWizard, 500)).current;

  useEffect(() => {
    if (!hasData()) {
      replace('Age');
    } else {
      fetchExpressWizard(getExistingData());
    }
  }, []);

  if (loading) {
    return (
      <div id="ExpressWizard_Output_Retirement" className="loading">
        <div className="loader">
          <p>Calculating results...</p>
          <i className="fa fa-sync-alt fa-spin" />
        </div>
      </div>
    );
  }

  return (
    <div id="ExpressWizard_Output_Retirement" className="adv-form ExpressWizard">
      <header>
        <FormMenu fetchExpressWizard={fetchExpressWizard} retirementAge={retirementAge} />
        <button
          className="startover"
          type="button"
          onClick={() => {
            clearData();
            replace('Age');
          }}
        >
          <i className="fa fa-sync-alt" />
          <span>Start Over</span>
        </button>
        <a href="http://advizr.com" className="logo" target="_blank" rel="noopener noreferrer"></a>
      </header>
      <h3>
        <p>
          If you retire at age{' '}
          <input
            className="underline"
            maxLength={2}
            type="text"
            value={retirementAge}
            placeholder={RETIREMENT_AGE}
            onChange={({ target: { value } }) => {
              setRetirementAge(value);
              if (!value) {
                debounceSetRetirementAge(getExistingData());
              } else {
                debounceSetRetirementAge(getExistingData(), value);
              }
            }}
          />
        </p>
        <p className="subtitle" dangerouslySetInnerHTML={{ __html: resultsData.SubtitleText }} />
      </h3>

      <div className="charts">
        <div id="ExpressWizard_RetirementCharts">
          <div className="ProgressChart">
            <div className="chart-header">
              <span>Retirement Savings Over Time</span>
              <ul>
                <li>
                  <i style={{ background: '#ffae3a' }} />
                  Current Plan
                </li>
                <li>
                  <i style={{ background: '#b7ecff' }} />
                  Sample Plan
                </li>
              </ul>
            </div>
            <div className="container">
              <div id="ExpressWizard_RetirementProgressChart">
                <RetirementChart
                  chartData={resultsData.RetirementChart.Data}
                  retirementAge={retirementAge}
                />
              </div>
            </div>
          </div>
          <div className="AllocationChart">
            <div className="chart-header">Hypothetical Allocation</div>
            <div className="container">
              <div id="ExpressWizard_AllocationChart">
                <div className="chart_container">
                  <AssetAllocationChart chartData={resultsData.AssetAllocationChart} />
                </div>
                <div className="labels">
                  <ul>
                    {Object.keys(resultsData.AssetAllocationChart).map((key, index) => (
                      <li key={key}>
                        <i style={{ background: ADV.settings.expresswizard_colors[index] }} />
                        {key}
                        <span>{Math.round(resultsData.AssetAllocationChart[key] * 100)}%</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="https://orionadvisortech.com"
                    className="disclosures"
                    onClick={event => {
                      event.preventDefault();
                      setDisclosureModalVisibility(true);
                    }}
                  >
                    Disclosures and Assumptions
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="cta">
        <p>Start making progress by implementing the right strategy!</p>
        <button
          type="button"
          className="primary flat"
          onClick={() => setIsContactModalVisible(true)}
        >
          Get Started
        </button>
      </div>

      <Disclosures
        isOpen={isDisclosureModalVisibility}
        onRequestClose={() => setDisclosureModalVisibility(false)}
        currentReturn={resultsData.CurrentReturn}
        recommendedReturn={resultsData.RecommendedReturn}
      />

      <ContactMenu
        isOpen={isContactModalVisible}
        onRequestClose={() => setIsContactModalVisible(false)}
        consumerDetails={{
          retirementAge,
          ...getExistingData(),
        }}
      />
    </div>
  );
};

export default Results;
